import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Check,
  ChevronDown,
  Coins,
  Copy,
  DollarSign,
  Download,
  ExternalLink,
  Filter,
  FlaskConical,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  Link2,
  MousePointerClick,
  Percent,
  Plus,
  RefreshCw,
  Send,
  Share2,
  Target,
  Trash2,
  TrendingUp,
  Users,
  Webhook,
  X,
} from 'lucide-react';
import { DatePicker } from './DatePicker';
import { ReferralQr } from '../components/ReferralQr';
import { renderQrDataUrl } from '../components/qrRender';
import { useCopy } from '../hooks/useCopy';
import { formatDateTime, formatInt, formatTon, shortenAddress } from '../lib/format';
import {
  buildPartnerPageLink,
  buildPartnerTeamTrackingLink,
  buildPartnerTrackingLink,
  buildStartappTrackingLink,
  buildSiteReferralLink,
  isTelegramMiniApp,
} from '../ton/referral';
import {
  PARTNER_API_AVAILABLE,
  PARTNER_EVENTS,
  type ActivityRow,
  type ApiKey,
  type CohortRow,
  type CommissionRow,
  type NetworkLevel,
  type Webhook as PartnerWebhook,
  type WebhookDelivery,
  createApiKey as apiCreateKey,
  createSource as apiCreateSource,
  createWebhook as apiCreateWebhook,
  deleteSource as apiDeleteSource,
  deleteWebhook as apiDeleteWebhook,
  fetchActivity,
  fetchApiKeys,
  fetchCohorts,
  fetchCommissions,
  fetchNetwork,
  fetchOverview,
  fetchWebhookDeliveries,
  fetchWebhooks,
  revokeApiKey as apiRevokeKey,
  savePostbacks,
  saveSourcePostbacks,
  addSpend as apiAddSpend,
  deleteSpend as apiDeleteSpend,
  fetchSpend,
  type OverviewRange,
  testPostback,
  testWebhook as apiTestWebhook,
  type EventConfig,
  type PartnerEvent,
  type PartnerEvents,
  type PartnerOverview,
  type PartnerSource,
  type SpendEntry,
  type SubStat,
} from './api';
import { AreaChart, Funnel, Sparkline, type FunnelStep, type TrendPoint } from './charts';
import { downloadCsv } from './csv';
import type { PartnerAuth } from './usePartnerAuth';
import { API_BASE } from '../ton/config';
import { SupportLink } from '../components/SupportLink';

interface PartnerDashboardProps {
  /** The partner's on-chain referral key. */
  refKey: number;
  referralsCount: number;
  referralEarnings: number; // TON
  auth: PartnerAuth;
}

type Tab = 'overview' | 'links' | 'creatives' | 'onchain' | 'postbacks' | 'api';
const DAY_RANGES = [7, 30, 90] as const;

/** Metrics selectable in the Overview trend chart. */
type TrendMetric = 'signups' | 'purchases' | 'volume' | 'earnings';

/** Sortable columns in the merged per-source table. */
type SubSortCol =
  | 'sub'
  | 'visitors'
  | 'purchases'
  | 'cr'
  | 'revenue'
  | 'epc'
  | 'cost'
  | 'roi';

/**
 * Sanitizes a traffic-SOURCE id to the deep-link grammar: letters + digits only
 * (the `_` is the link separator), lower-cased, up to 32 chars. Mirrors the
 * backend's SOURCE_RE so the built link and the server agree.
 */
const sanitizeSource = (raw: string): string =>
  raw.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 32);

// The indexer stores tx hashes base64-encoded; tonviewer's /transaction route
// expects hex, so convert before linking.
const b64ToHex = (b64: string): string => {
  const bin = atob(b64);
  let hex = '';
  for (let i = 0; i < bin.length; i += 1) hex += bin.charCodeAt(i).toString(16).padStart(2, '0');
  return hex;
};
const tonviewerTx = (hashB64: string): string =>
  `https://tonviewer.com/transaction/${b64ToHex(hashB64)}`;

type PromoLink = 'site' | 'bot' | 'app' | 'team' | 'partner';
const PROMO_LINKS: PromoLink[] = ['site', 'bot', 'app', 'team', 'partner'];

/**
 * Default per-link-type QR source labels. A QR is static, so we bake a fixed
 * source (never a dynamic click_id) into it; each destination gets its own
 * label so QR-driven traffic is distinguishable in the stats. The partner can
 * override this with one of their own named sources (then every QR shares it).
 */
const QR_SOURCE_AUTO = 'auto';
const QR_DEFAULT_SOURCE: Record<PromoLink, string> = {
  bot: 'qr4bot',
  app: 'qr4app',
  team: 'qr4aibot',
  site: 'qr4site',
  partner: 'qr4partners',
};

const nano = (s: string | undefined): number => (s ? Number(s) / 1e9 : 0);
const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0);

/** Exact period-over-period percentage change between two totals. */
function deltaPct(cur: number, prev: number): number | null {
  if (prev === 0) return cur > 0 ? 100 : null;
  return ((cur - prev) / prev) * 100;
}

function CopyLinkRow({ label, hint, link }: { label: string; hint?: string; link: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  return (
    <div className="pl-link">
      <div className="pl-link__head">
        <span className="pl-link__label">{label}</span>
        {hint ? <span className="pl-link__hint">{hint}</span> : null}
      </div>
      <div className="pl-link__row">
        <code className="pl-link__url">{link}</code>
        <button
          type="button"
          className="pl-btn pl-btn--ghost"
          onClick={() => void copy(link)}
          aria-label={t('refLink.copyAria', { label })}
        >
          {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
          {copied ? t('refLink.copied') : t('refLink.copy')}
        </button>
      </div>
    </div>
  );
}

/** Colour class for a postback HTTP status: green 2xx, red otherwise, grey none. */
const statusClass = (s: number | null): string =>
  s == null ? 'is-none' : s >= 200 && s < 300 ? 'is-ok' : 'is-bad';

type TestResult = { status: number | null; error?: string } | 'testing';
type TestResults = Record<string, TestResult>;

/**
 * The 7-event postback editor (a toggle + URL + test button per conversion
 * event). Reused by both the main-source card and each named-source card;
 * `scope` namespaces the shared test-result map so cards don't clash, and
 * `disabled` greys out the inputs when a source inherits main's postbacks.
 */
function PostbackEditor({
  events,
  onEvent,
  disabled,
  scope,
  onTest,
  testResults,
}: {
  events: PartnerEvents;
  onEvent: (event: PartnerEvent, patch: Partial<EventConfig>) => void;
  disabled?: boolean;
  scope: string;
  onTest: (key: string, url: string) => void;
  testResults: TestResults;
}) {
  const { t } = useTranslation();
  return (
    <div className="pl-events">
      {PARTNER_EVENTS.map((event) => {
        const cfg = events[event] ?? { enabled: false, url: '' };
        const key = `${scope}:${event}`;
        const res = testResults[key];
        return (
          <div key={event} className="pl-event">
            <label className="pl-event__toggle">
              <input
                type="checkbox"
                checked={cfg.enabled}
                disabled={disabled}
                onChange={(e) => onEvent(event, { enabled: e.target.checked })}
              />
              <span>{t(`partner.events.${event}`)}</span>
            </label>
            <div className="pl-event__row">
              <input
                type="url"
                className="pl-event__url"
                placeholder={t(
                  event === 'ticket_purchase'
                    ? 'partner.postbacks.urlPlaceholderPurchase'
                    : 'partner.postbacks.urlPlaceholder',
                )}
                value={cfg.url}
                disabled={disabled}
                onChange={(e) => onEvent(event, { url: e.target.value })}
                spellCheck={false}
              />
              <button
                type="button"
                className="pl-btn pl-btn--ghost"
                onClick={() => onTest(key, cfg.url)}
                disabled={disabled || !cfg.url.trim() || res === 'testing'}
              >
                <FlaskConical size={14} aria-hidden="true" /> {t('partner.postbacks.test')}
              </button>
            </div>
            {res && res !== 'testing' && (
              <span className={`pl-test-result ${statusClass(res.status)}`}>
                {res.status != null
                  ? t('partner.postbacks.testStatus', { status: res.status })
                  : t('partner.postbacks.testFailed', { error: res.error ?? 'error' })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One named-source postback card: collapsible, starts collapsed. A partner
 * either configures the source's own postback URLs or ticks "use main" to
 * inherit the main campaign's postbacks (which greys out the inputs). Holds its
 * own draft state and saves independently; `onSaved` syncs the parent list.
 */
function SourcePostbackCard({
  source,
  token,
  onSaved,
  onTest,
  testResults,
}: {
  source: PartnerSource;
  token: string;
  onSaved: (s: PartnerSource) => void;
  onTest: (key: string, url: string) => void;
  testResults: TestResults;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<PartnerEvents>(source.events);
  const [useMain, setUseMain] = useState(source.useMain);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  const setEvent = (event: PartnerEvent, patch: Partial<EventConfig>) => {
    setSaveState('idle');
    setEvents((prev) => ({
      ...prev,
      [event]: { ...(prev[event] ?? { enabled: false, url: '' }), ...patch },
    }));
  };

  const handleSave = async () => {
    setSaveState('saving');
    try {
      const updated = await saveSourcePostbacks(token, source.id, events, useMain);
      setEvents(updated.events);
      setUseMain(updated.useMain);
      onSaved(updated);
      setSaveState('saved');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'error');
      setSaveState('error');
    }
  };

  const toggle = () => setOpen((v) => !v);
  return (
    <div className={`pl-src pl-src--toggle${open ? ' is-open' : ''}`}>
      <div
        className="pl-src__head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <ChevronDown size={16} aria-hidden="true" className="pl-src__chev" />
        <span className="pl-src__label">{source.label}</span>
        <code className="pl-src__sub">{source.subId}</code>
        {useMain && <span className="pl-src__tag">{t('partner.postbacks.usingMain')}</span>}
      </div>
      {open && (
        <div className="pl-src__body">
          <label className="pl-usemain">
            <input
              type="checkbox"
              checked={useMain}
              onChange={(e) => {
                setSaveState('idle');
                setUseMain(e.target.checked);
              }}
            />
            <span>{t('partner.postbacks.useMain')}</span>
          </label>
          <PostbackEditor
            events={events}
            onEvent={setEvent}
            disabled={useMain}
            scope={`src${source.id}`}
            onTest={onTest}
            testResults={testResults}
          />
          <div className="pl-save-row">
            <button
              type="button"
              className="pl-btn pl-btn--gold"
              onClick={() => void handleSave()}
              disabled={saveState === 'saving'}
            >
              {saveState === 'saved' ? (
                <>
                  <Check size={15} aria-hidden="true" /> {t('partner.postbacks.saved')}
                </>
              ) : (
                t('partner.postbacks.save')
              )}
            </button>
            {saveState === 'error' && (
              <span className="pl-error">{t('partner.postbacks.saveError', { error: saveError })}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A read-only monospace value with a copy button (API keys, secrets, URLs). */
function CopyValue({ value }: { value: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  return (
    <div className="pl-link__row">
      <code className="pl-link__url">{value}</code>
      <button type="button" className="pl-btn pl-btn--ghost" onClick={() => void copy(value)}>
        {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
        {copied ? t('refLink.copied') : t('refLink.copy')}
      </button>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  spark,
  delta,
  color,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  spark?: number[];
  delta?: number | null;
  color?: string;
}) {
  const up = (delta ?? 0) >= 0;
  return (
    <div className="pl-kpi">
      <div className="pl-kpi__top">
        <span className="pl-kpi__icon">{icon}</span>
        {delta != null && (
          <span className={`pl-kpi__delta ${up ? 'is-up' : 'is-down'}`}>
            {up ? (
              <ArrowUpRight size={13} aria-hidden="true" />
            ) : (
              <ArrowDownRight size={13} aria-hidden="true" />
            )}
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      <span className="pl-kpi__value">{value}</span>
      <span className="pl-kpi__label">{label}</span>
      {spark && spark.length > 1 && (
        <div className="pl-kpi__spark">
          <Sparkline values={spark} color={color} />
        </div>
      )}
    </div>
  );
}

export function PartnerDashboard({ refKey, referralsCount, auth }: PartnerDashboardProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('overview');
  const [days, setDays] = useState<number>(30);
  // Custom from/to window (UTC dates). When set it overrides the day presets.
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  // Overview traffic-source filter (sub_id); '' = all sources (aggregated).
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [overview, setOverview] = useState<PartnerOverview | null>(null);
  const [loading, setLoading] = useState(false);
  // Overview trend metric + dated per-source spend entries.
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('purchases');
  const [spend, setSpend] = useState<SpendEntry[]>([]);
  // SubID whose "manage spend" modal is open (null = closed) + the add-entry draft.
  const [spendModal, setSpendModal] = useState<string | null>(null);
  const [spendDraft, setSpendDraft] = useState({ amount: '', from: '', to: '' });
  const [spendBusy, setSpendBusy] = useState(false);
  const [spendError, setSpendError] = useState('');
  // Draft values for the custom from/to date inputs.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [subId, setSubId] = useState('main');
  // Whether copied tracking links carry the {click_id} macro (only useful when
  // the partner substitutes it via an ad network / external tracker).
  const [withClickId, setWithClickId] = useState(true);
  const [events, setEvents] = useState<PartnerEvents>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  // Named sources (saved SubID links). `customMode` toggles the link builder
  // from picking a saved source to typing an arbitrary SubID; `newLabel` seeds
  // the "save this custom SubID as a named source" affordance.
  const [sources, setSources] = useState<PartnerSource[]>([]);
  const [customMode, setCustomMode] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [srcBusy, setSrcBusy] = useState(false);
  const [srcError, setSrcError] = useState('');

  // Per-source table sort.
  const [sortCol, setSortCol] = useState<SubSortCol>('visitors');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Live feed + on-chain commissions + postback test.
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [overviewActivity, setOverviewActivity] = useState<ActivityRow[]>([]);
  const [commissions, setCommissions] = useState<CommissionRow[] | null>(null);
  // Date range (UTC, 'YYYY-MM-DD' or '') for the commissions CSV export.
  const [commExportFrom, setCommExportFrom] = useState('');
  const [commExportTo, setCommExportTo] = useState('');

  // Growth: downline network + acquisition cohorts (lazy-loaded).
  const [network, setNetwork] = useState<NetworkLevel[] | null | undefined>(undefined);
  const [cohorts, setCohorts] = useState<CohortRow[] | null>(null);

  // Creatives: which link the promo QR is built around, and the source baked
  // into it ('auto' = per-type qr4* label, otherwise one of the partner's own
  // named sources shared across every QR).
  const [promoLink, setPromoLink] = useState<PromoLink>('site');
  const [qrSource, setQrSource] = useState<string>(QR_SOURCE_AUTO);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Full-size QR shown for long-press saving where downloads don't work
  // (the Telegram Mini App webview silently ignores anchor downloads).
  const [qrSavePreview, setQrSavePreview] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<TestResults>({});

  // API tab: keys + webhooks (lazy-loaded).
  const [apiKeys, setApiKeys] = useState<ApiKey[] | null>(null);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeySecret, setNewKeySecret] = useState('');
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [webhooks, setWebhooks] = useState<PartnerWebhook[] | null>(null);
  const [newHookUrl, setNewHookUrl] = useState('');
  const [newHookEvents, setNewHookEvents] = useState<PartnerEvent[]>([]);
  const [hookBusy, setHookBusy] = useState(false);
  const [hookError, setHookError] = useState('');
  const [hookTest, setHookTest] = useState<
    Record<string, { status: number | null; error?: string } | 'testing'>
  >({});
  const [deliveries, setDeliveries] = useState<Record<string, WebhookDelivery[]>>({});

  const { token, clearToken } = auth;
  const load = useCallback(
    async (range: OverviewRange) => {
      if (!PARTNER_API_AVAILABLE || !token) return;
      setLoading(true);
      try {
        const activityRequest = fetchActivity(token, 30).catch(() => [] as ActivityRow[]);
        const feedRequest = range.source
          ? fetchActivity(token, 30, range.source).catch(() => [] as ActivityRow[])
          : activityRequest;
        const [data, act, feed, spendRows] = await Promise.all([
          fetchOverview(token, range),
          activityRequest,
          feedRequest,
          fetchSpend(token).catch(() => [] as SpendEntry[]),
        ]);
        setOverview(data);
        setActivity(act);
        setOverviewActivity(feed);
        setSpend(spendRows);
        if (data.campaign) setEvents(data.campaign.events);
        if (data.sources) setSources(data.sources);
      } catch (err) {
        if (err instanceof Error && err.message === 'unauthorized') clearToken();
      } finally {
        setLoading(false);
      }
    },
    [token, clearToken],
  );

  const activeRange: OverviewRange = useMemo(
    () => ({
      ...(customRange ? { from: customRange.from, to: customRange.to } : { days }),
      source: sourceFilter || undefined,
    }),
    [customRange, days, sourceFilter],
  );

  useEffect(() => {
    void load(activeRange);
  }, [load, activeRange]);

  // Lazy-load on-chain commissions the first time the Network tab opens.
  useEffect(() => {
    if (tab !== 'onchain' || !token || commissions !== null) return;
    fetchCommissions(token, 10000)
      .then(setCommissions)
      .catch(() => setCommissions([]));
  }, [tab, token, commissions]);

  // Lazy-load downline network + cohorts the first time the Growth tab opens.
  useEffect(() => {
    if (tab !== 'onchain' || !token) return;
    if (network === undefined) fetchNetwork(token).then(setNetwork).catch(() => setNetwork(null));
    if (cohorts === null) fetchCohorts(token, 26).then(setCohorts).catch(() => setCohorts([]));
  }, [tab, token, network, cohorts]);

  // Lazy-load API keys + webhooks the first time the API tab opens.
  useEffect(() => {
    if (tab !== 'api' || !token) return;
    if (apiKeys === null) fetchApiKeys(token).then(setApiKeys).catch(() => setApiKeys([]));
    if (webhooks === null) fetchWebhooks(token).then(setWebhooks).catch(() => setWebhooks([]));
  }, [tab, token, apiKeys, webhooks]);

  const handleTest = async (key: string, url: string) => {
    if (!token || !url.trim()) return;
    setTestResults((prev) => ({ ...prev, [key]: 'testing' }));
    try {
      const result = await testPostback(token, url.trim());
      setTestResults((prev) => ({ ...prev, [key]: result }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [key]: { status: null, error: err instanceof Error ? err.message : 'error' },
      }));
    }
  };

  // Sync a named source back into the list after its postback config is saved.
  const handleSourceSaved = (updated: PartnerSource) => {
    setSources((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const exportSubsCsv = () => {
    const header = [
      'sub_id',
      'visitors',
      'purchases',
      'cr_percent',
      'revenue_usd',
      'epc_usd',
      'cost_usd',
      'roi_percent',
      'cpa_usd',
      ...PARTNER_EVENTS.slice(0, 5),
    ];
    const num = (v: number | null): string => (v == null ? '' : (Math.round(v * 100) / 100).toString());
    const rows = visibleSubs.map((s) => [
      s.subId,
      s.visitors,
      s.purchases,
      num(s.cr),
      num(s.revenueUsd),
      num(s.epc),
      num(s.costUsd),
      num(s.roi),
      num(s.cpa),
      ...PARTNER_EVENTS.slice(0, 5).map((e) => s.events[e]?.total ?? 0),
    ]);
    downloadCsv(`pyramida-subids-${refKey}.csv`, [header, ...rows]);
  };

  const exportCommissionsCsv = () => {
    // Inclusive UTC bounds; empty field = unbounded on that side.
    const fromTs = commExportFrom ? Date.parse(`${commExportFrom}T00:00:00Z`) / 1000 : null;
    const toTs = commExportTo ? Date.parse(`${commExportTo}T23:59:59Z`) / 1000 : null;
    const header = ['datetime_utc', 'buyer', 'amount_ton', 'tx_hash'];
    const rows = (commissions ?? [])
      .filter((c) => (fromTs === null || c.utime >= fromTs) && (toTs === null || c.utime <= toTs))
      .map((c) => [
        new Date(c.utime * 1000).toISOString(),
        c.buyer ?? '',
        (Number(c.amountNano) / 1e9).toString(),
        c.txHash,
      ]);
    const suffix =
      commExportFrom || commExportTo ? `-${commExportFrom || 'start'}_${commExportTo || 'now'}` : '';
    downloadCsv(`pyramida-commissions-${refKey}${suffix}.csv`, [header, ...rows]);
  };

  const links = useMemo(() => {
    const source = sanitizeSource(subId) || 'main';
    const opts = { clickId: withClickId };
    return {
      bot: buildPartnerTrackingLink(refKey, source, opts),
      app: buildStartappTrackingLink(refKey, source, opts),
      team: buildPartnerTeamTrackingLink(refKey, source, opts),
      site: buildSiteReferralLink(refKey, source, opts),
      partner: buildPartnerPageLink(refKey, source, opts),
    };
  }, [refKey, subId, withClickId]);

  // QR links: static, so no {click_id} macro. The source is either the per-type
  // default (qr4bot/qr4aibot/qr4site/qr4partners) or a single named source the
  // partner picked (shared across every destination).
  const qrLinks = useMemo(() => {
    const pick = (lt: PromoLink): string =>
      qrSource === QR_SOURCE_AUTO ? QR_DEFAULT_SOURCE[lt] : qrSource;
    const noCid = { clickId: false };
    return {
      bot: buildPartnerTrackingLink(refKey, pick('bot'), noCid),
      app: buildStartappTrackingLink(refKey, pick('app'), noCid),
      team: buildPartnerTeamTrackingLink(refKey, pick('team'), noCid),
      site: buildSiteReferralLink(refKey, pick('site'), noCid),
      partner: buildPartnerPageLink(refKey, pick('partner'), noCid),
    };
  }, [refKey, qrSource]);

  const setEvent = (event: PartnerEvent, patch: Partial<{ enabled: boolean; url: string }>) => {
    setSaveState('idle');
    setEvents((prev) => {
      const cur = prev[event] ?? { enabled: false, url: '' };
      return { ...prev, [event]: { ...cur, ...patch } };
    });
  };

  const handleSave = async () => {
    if (!token) return;
    setSaveState('saving');
    try {
      const campaign = await savePostbacks(token, events);
      setEvents(campaign.events);
      setSaveState('saved');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'error');
      setSaveState('error');
    }
  };

  // Persist the currently-typed custom SubID as a named source, then select it.
  const handleSaveCustomSource = async () => {
    if (!token) return;
    setSrcError('');
    setSrcBusy(true);
    try {
      const src = await apiCreateSource(token, newLabel, sanitizeSource(subId));
      setSources((prev) => [...prev.filter((s) => s.id !== src.id), src]);
      setNewLabel('');
      setCustomMode(false);
      setSubId(src.subId);
    } catch (err) {
      setSrcError(err instanceof Error ? err.message : 'error');
    } finally {
      setSrcBusy(false);
    }
  };

  const handleDeleteSource = async (id: number) => {
    if (!token) return;
    setSources((prev) => prev.filter((s) => s.id !== id));
    pickSource('main'); // the deleted source was loaded in the builder; reset
    try {
      await apiDeleteSource(token, id);
    } catch {
      void load(activeRange); // resync on failure
    }
  };

  // ── API keys ──────────────────────────────────────────────────────────────
  const handleCreateKey = async () => {
    if (!token) return;
    setKeyError('');
    setKeyBusy(true);
    try {
      const created = await apiCreateKey(token, newKeyLabel.trim());
      setNewKeySecret(created.apiKey);
      setNewKeyLabel('');
      const keys = await fetchApiKeys(token);
      setApiKeys(keys);
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : 'error');
    } finally {
      setKeyBusy(false);
    }
  };

  const handleRevokeKey = async (id: string) => {
    if (!token) return;
    setApiKeys((prev) => (prev ?? []).filter((k) => k.id !== id));
    try {
      await apiRevokeKey(token, id);
    } catch {
      fetchApiKeys(token).then(setApiKeys).catch(() => undefined);
    }
  };

  // ── Webhooks ────────────────────────────────────────────────────────────────
  const toggleHookEvent = (event: PartnerEvent) =>
    setNewHookEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );

  const handleCreateWebhook = async () => {
    if (!token || !newHookUrl.trim()) return;
    setHookError('');
    setHookBusy(true);
    try {
      const hook = await apiCreateWebhook(token, newHookUrl.trim(), newHookEvents);
      setWebhooks((prev) => [hook, ...(prev ?? [])]);
      setNewHookUrl('');
      setNewHookEvents([]);
    } catch (err) {
      setHookError(err instanceof Error ? err.message : 'error');
    } finally {
      setHookBusy(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    if (!token) return;
    setWebhooks((prev) => (prev ?? []).filter((w) => w.id !== id));
    try {
      await apiDeleteWebhook(token, id);
    } catch {
      fetchWebhooks(token).then(setWebhooks).catch(() => undefined);
    }
  };

  const handleTestWebhook = async (id: string) => {
    if (!token) return;
    setHookTest((prev) => ({ ...prev, [id]: 'testing' }));
    try {
      const result = await apiTestWebhook(token, id);
      setHookTest((prev) => ({ ...prev, [id]: result }));
      fetchWebhookDeliveries(token, id, 20)
        .then((d) => setDeliveries((prev) => ({ ...prev, [id]: d })))
        .catch(() => undefined);
    } catch (err) {
      setHookTest((prev) => ({
        ...prev,
        [id]: { status: null, error: err instanceof Error ? err.message : 'error' },
      }));
    }
  };

  const handleToggleDeliveries = async (id: string) => {
    if (!token) return;
    if (deliveries[id]) {
      setDeliveries((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    try {
      const d = await fetchWebhookDeliveries(token, id, 20);
      setDeliveries((prev) => ({ ...prev, [id]: d }));
    } catch {
      setDeliveries((prev) => ({ ...prev, [id]: [] }));
    }
  };

  const funnel = overview?.funnel ?? null;
  const subStats = useMemo(() => overview?.subStats ?? [], [overview]);
  const series = overview?.series ?? [];
  const prevSeries = overview?.prevSeries ?? [];
  const hasApi = PARTNER_API_AVAILABLE && Boolean(token);

  // Link-builder source selection. The typed custom SubID can be saved as a
  // named source when it's non-empty, isn't the reserved default, and isn't
  // already one of the saved sources.
  const savedSubIds = useMemo(() => new Set(sources.map((s) => s.subId)), [sources]);
  const trimmedSub = sanitizeSource(subId).trim();
  const canSaveCustom =
    hasApi &&
    customMode &&
    trimmedSub.length > 0 &&
    trimmedSub !== 'main' &&
    !savedSubIds.has(trimmedSub);
  const pickSource = (value: string) => {
    setCustomMode(false);
    setSubId(value);
  };
  // The saved source currently loaded in the builder (null for the default,
  // an unsaved custom SubID, or custom mode). Drives the inline delete button.
  const activeSource = useMemo(
    () => (customMode ? null : (sources.find((s) => s.subId === trimmedSub) ?? null)),
    [customMode, sources, trimmedSub],
  );
  const enterCustomMode = () => {
    setSrcError('');
    setNewLabel('');
    setCustomMode(true);
    setSubId('');
  };

  // Live GRAM/USD rate + L1 commission share drive all USD (revenue/EPC/ROI) math.
  const rate = overview?.tonUsdRate ?? null;
  const sharePct = overview?.referralSharePercent ?? 20;
  /**
   * Partner commission (USD) earned on a window's referred revenue. Prefers the
   * backend's exact per-event figure (lottery buys and bets/deposits carry
   * different L1 shares); falls back to the flat lottery share × volume when an
   * older backend doesn't send it.
   */
  const commissionUsd = useCallback(
    (volumeNano: string, commissionNano?: string): number | null => {
      if (rate == null) return null;
      const gram =
        commissionNano != null ? nano(commissionNano) : (nano(volumeNano) * sharePct) / 100;
      return gram * rate;
    },
    [rate, sharePct],
  );
  /** Commission in GRAM for chart points (same fallback rule). */
  const commissionGram = useCallback(
    (volumeNano: string, commissionNano?: string): number =>
      commissionNano != null ? nano(commissionNano) : (nano(volumeNano) * sharePct) / 100,
    [sharePct],
  );

  const onchain = overview?.onchain;
  const prev = overview?.prev;
  const visitorsTotal = useMemo(() => sum(subStats.map((s) => s.visitors)), [subStats]);
  // Visitor base for CR%/EPC: the selected source's visitors when filtering,
  // otherwise the sum across all sources. (On-chain KPIs come pre-scoped from
  // the backend; visitors live only in the always-full per-source table.)
  const metricVisitors = useMemo(
    () =>
      sourceFilter
        ? (subStats.find((s) => s.subId === sourceFilter)?.visitors ?? 0)
        : visitorsTotal,
    [sourceFilter, subStats, visitorsTotal],
  );
  const purchasesTotal = onchain?.purchases ?? 0;
  const volumeTon = nano(onchain?.volumeNano);
  const revenueUsd = commissionUsd(onchain?.volumeNano ?? '0', onchain?.commissionNano);
  const prevRevenueUsd = commissionUsd(prev?.volumeNano ?? '0', prev?.commissionNano);
  const conversionPct = metricVisitors > 0 ? (purchasesTotal / metricVisitors) * 100 : null;
  const avgCheckTon = purchasesTotal > 0 ? volumeTon / purchasesTotal : null;
  const epcUsd = revenueUsd != null && metricVisitors > 0 ? revenueUsd / metricVisitors : null;

  const signupsSpark = series.map((p) => p.signups);
  const purchasesSpark = series.map((p) => p.purchases);
  const volumeSpark = series.map((p) => nano(p.volumeNano));

  // Selected-metric daily trend + previous-period overlay (same length window).
  const metricValue = useCallback(
    (p: {
      signups: number;
      purchases: number;
      volumeNano: string;
      commissionNano?: string;
    }): number => {
      switch (trendMetric) {
        case 'signups':
          return p.signups;
        case 'volume':
          return nano(p.volumeNano);
        case 'earnings':
          return commissionGram(p.volumeNano, p.commissionNano);
        default:
          return p.purchases;
      }
    },
    [trendMetric, commissionGram],
  );
  const trendPoints: TrendPoint[] = series.map((p) => ({ date: p.date, value: metricValue(p) }));
  const prevTrend: number[] = prevSeries.map((p) => metricValue(p));
  const trendFmt = (v: number): string =>
    trendMetric === 'volume' || trendMetric === 'earnings' ? formatTon(v) : formatInt(v);

  const funnelSteps: FunnelStep[] = funnel
    ? [
        { label: t('partner.panel.invited'), value: funnel.invited },
        { label: t('partner.panel.appOpen'), value: funnel.appOpen },
        { label: t('partner.panel.wallets'), value: funnel.walletAuth },
        { label: t('partner.panel.purchases'), value: funnel.ticketPurchase },
        { label: t('partner.panel.secondGen'), value: funnel.secondGen },
      ]
    : [];

  interface SubRow extends SubStat {
    cr: number | null; // visit → purchase, %
    revenueUsd: number | null; // partner commission, USD
    epc: number | null; // revenue / visitor, USD
    roi: number | null; // (revenue − cost) / cost, %
    cpa: number | null; // cost / purchase, USD
  }

  const subRows: SubRow[] = useMemo(
    () =>
      subStats.map((s) => {
        const rev = commissionUsd(s.revenueNano, s.commissionNano);
        const cr = s.visitors > 0 ? (s.purchases / s.visitors) * 100 : null;
        const epc = rev != null && s.visitors > 0 ? rev / s.visitors : null;
        const roi = rev != null && s.costUsd > 0 ? ((rev - s.costUsd) / s.costUsd) * 100 : null;
        const cpa = s.costUsd > 0 && s.purchases > 0 ? s.costUsd / s.purchases : null;
        return { ...s, cr, revenueUsd: rev, epc, roi, cpa };
      }),
    [subStats, commissionUsd],
  );

  const sortedSubs = useMemo(() => {
    const key = (r: SubRow): number | string => {
      switch (sortCol) {
        case 'sub':
          return r.subId;
        case 'purchases':
          return r.purchases;
        case 'cr':
          return r.cr ?? -1;
        case 'revenue':
          return r.revenueUsd ?? -1;
        case 'epc':
          return r.epc ?? -1;
        case 'cost':
          return r.costUsd;
        case 'roi':
          return r.roi ?? -Infinity;
        default:
          return r.visitors;
      }
    };
    const arr = [...subRows];
    arr.sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      const cmp = typeof ka === 'string' ? ka.localeCompare(kb as string) : ka - (kb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [subRows, sortCol, sortDir]);

  // When a source filter is active, the per-source table narrows to that one
  // row (everything else on the page is already scoped); otherwise show all.
  const visibleSubs = useMemo(
    () => (sourceFilter ? sortedSubs.filter((r) => r.subId === sourceFilter) : sortedSubs),
    [sortedSubs, sourceFilter],
  );

  // Chips for the overview source filter: only "all" (implicit) + main + the
  // sources the partner explicitly created. Traffic-discovered sub_ids are NOT
  // listed — they roll up into "all" (and mis-parsed / source-less clicks fall
  // under main), matching how the sources page presents them.
  const sourceOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { subId: string; label: string }[] = [];
    const add = (subId: string, label: string) => {
      if (!subId || seen.has(subId)) return;
      seen.add(subId);
      opts.push({ subId, label });
    };
    add('main', t('partner.sources.mainLabel'));
    sources.forEach((s) => add(s.subId, s.label.trim() || s.subId));
    return opts;
  }, [sources, t]);

  const toggleSort = (col: SubSortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir(col === 'sub' ? 'asc' : 'desc');
    }
  };

  // Open/close the dated-spend manager for a source. Opening seeds the add form
  // with the currently selected reporting window.
  const openSpendModal = (subIdKey: string) => {
    setSpendError('');
    setSpendDraft({
      amount: '',
      from: overview?.from ?? todayStr,
      to: overview?.to ?? todayStr,
    });
    setSpendModal(subIdKey);
  };
  const closeSpendModal = () => {
    setSpendModal(null);
    setSpendError('');
  };

  // Record a dated spend entry, then refresh so the window cost + ROI recompute.
  const handleAddSpend = async () => {
    if (!token || !spendModal) return;
    const val = Number(spendDraft.amount.replace(',', '.').trim());
    const { from, to } = spendDraft;
    if (!Number.isFinite(val) || val < 0) {
      setSpendError(t('partner.spend.errAmount'));
      return;
    }
    if (!from || !to || from > to) {
      setSpendError(t('partner.spend.errDates'));
      return;
    }
    setSpendBusy(true);
    setSpendError('');
    try {
      await apiAddSpend(token, {
        subId: spendModal,
        fromDate: from,
        toDate: to,
        costUsd: Math.round(val * 100) / 100,
      });
      const [rows] = await Promise.all([fetchSpend(token), load(activeRange)]);
      setSpend(rows);
      setSpendDraft((d) => ({ ...d, amount: '' }));
    } catch {
      setSpendError(t('partner.spend.errSave'));
    } finally {
      setSpendBusy(false);
    }
  };

  const handleDeleteSpend = async (id: number) => {
    if (!token) return;
    setSpend((prev) => prev.filter((e) => e.id !== id));
    try {
      await apiDeleteSpend(token, id);
      await load(activeRange);
    } catch {
      void fetchSpend(token).then(setSpend).catch(() => {});
    }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const applyCustomRange = () => {
    if (dateFrom && dateTo && dateFrom <= dateTo) setCustomRange({ from: dateFrom, to: dateTo });
  };
  const clearCustomRange = () => {
    setCustomRange(null);
    setDateFrom('');
    setDateTo('');
  };

  const tabs: { id: Tab; label: string; icon: ReactNode }[] = [
    { id: 'overview', label: t('partner.tabs.overview'), icon: <LayoutDashboard size={16} /> },
    { id: 'links', label: t('partner.tabs.links'), icon: <Link2 size={16} /> },
    { id: 'creatives', label: t('partner.tabs.creatives'), icon: <ImageIcon size={16} /> },
    { id: 'onchain', label: t('partner.tabs.onchain'), icon: <Coins size={16} /> },
    { id: 'postbacks', label: t('partner.tabs.postbacks'), icon: <Webhook size={16} /> },
    { id: 'api', label: t('partner.tabs.api'), icon: <KeyRound size={16} /> },
  ];

  const eventLabel = (event: string): string =>
    (PARTNER_EVENTS as string[]).includes(event) ? t(`partner.events.${event}`) : event;

  const showSkeleton = hasApi && loading && !overview;

  // The delivery log shows only events that actually fired a postback; the
  // Overview "Live activity" feed keeps showing every recorded conversion.
  const firedActivity = activity.filter((a) => a.fired);

  return (
    <section className="pl-panel" id="panel">
      <div className="pl-panel__head">
        <h2 className="pl-h2">
          <LayoutDashboard size={22} aria-hidden="true" /> {t('partner.panel.title')}
        </h2>
        <div className="pl-panel__key">
          <KeyRound size={16} aria-hidden="true" />
          <span>{t('partner.panel.keyLabel')}</span>
          <strong>#{refKey}</strong>
        </div>
      </div>

      {/* Tab navigation */}
      <nav className="pl-tabs" role="tablist" aria-label={t('partner.panel.title')}>
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            className={`pl-tab ${tab === tb.id ? 'is-active' : ''}`}
            onClick={() => setTab(tb.id)}
          >
            {tb.icon}
            {tb.label}
          </button>
        ))}
      </nav>

      {/* ── OVERVIEW ─────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="pl-tabpanel" role="tabpanel">
          {hasApi && (
            <div className="pl-toolbar">
              <div className="pl-range" role="group" aria-label={t('partner.range.label')}>
                {DAY_RANGES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`pl-range__btn ${!customRange && days === d ? 'is-active' : ''}`}
                    onClick={() => {
                      setCustomRange(null);
                      setDays(d);
                    }}
                  >
                    {t('partner.range.days', { count: d })}
                  </button>
                ))}
              </div>
              <div className="pl-daterange" role="group" aria-label={t('partner.range.customLabel')}>
                <DatePicker
                  value={dateFrom}
                  max={dateTo || todayStr}
                  ariaLabel={t('partner.range.from')}
                  placeholder={t('partner.range.from')}
                  onChange={setDateFrom}
                />
                <span className="pl-daterange__sep">–</span>
                <DatePicker
                  value={dateTo}
                  min={dateFrom || undefined}
                  max={todayStr}
                  ariaLabel={t('partner.range.to')}
                  placeholder={t('partner.range.to')}
                  onChange={setDateTo}
                />
                <button
                  type="button"
                  className="pl-btn pl-btn--ghost"
                  onClick={applyCustomRange}
                  disabled={!dateFrom || !dateTo || dateFrom > dateTo}
                >
                  {t('partner.range.apply')}
                </button>
                {customRange && (
                  <button type="button" className="pl-btn pl-btn--ghost" onClick={clearCustomRange}>
                    {t('partner.range.reset')}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pl-btn pl-btn--ghost"
                onClick={() => void load(activeRange)}
                disabled={loading}
              >
                <RefreshCw
                  size={15}
                  aria-hidden="true"
                  className={loading ? 'pl-spin' : undefined}
                />
                {t('partner.panel.refresh')}
              </button>
            </div>
          )}

          {/* Traffic-source filter: scopes every metric below to one source. */}
          {hasApi && sourceOptions.length > 0 && (
            <div
              className="pl-srcfilter"
              role="group"
              aria-label={t('partner.overview.sourceFilter')}
            >
              <span className="pl-srcfilter__label">
                <Filter size={14} aria-hidden="true" /> {t('partner.overview.sourceFilter')}
              </span>
              <div className="pl-srcpick">
                <button
                  type="button"
                  className={`pl-srcpick__chip${!sourceFilter ? ' is-active' : ''}`}
                  onClick={() => setSourceFilter('')}
                >
                  {t('partner.overview.allSources')}
                </button>
                {sourceOptions.map((o) => (
                  <button
                    key={o.subId}
                    type="button"
                    className={`pl-srcpick__chip${sourceFilter === o.subId ? ' is-active' : ''}`}
                    onClick={() => setSourceFilter(o.subId)}
                  >
                    {o.label}
                    <code>{o.subId}</code>
                  </button>
                ))}
              </div>
            </div>
          )}

          {showSkeleton ? (
            <div className="pl-kpis">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="pl-kpi pl-skeleton" aria-hidden="true" />
              ))}
            </div>
          ) : (
            <div className="pl-kpis">
              <Kpi
                icon={<Users size={18} aria-hidden="true" />}
                label={t('partner.panel.signups')}
                value={formatInt(onchain?.signups ?? referralsCount)}
                spark={signupsSpark}
                delta={prev ? deltaPct(onchain?.signups ?? 0, prev.signups) : null}
              />
              <Kpi
                icon={<BarChart3 size={18} aria-hidden="true" />}
                label={t('partner.overview.metric.purchases')}
                value={formatInt(purchasesTotal)}
                spark={purchasesSpark}
                delta={prev ? deltaPct(purchasesTotal, prev.purchases) : null}
              />
              <Kpi
                icon={<TrendingUp size={18} aria-hidden="true" />}
                label={t('partner.overview.volume')}
                value={`${formatTon(volumeTon)} GRAM`}
                spark={volumeSpark}
                delta={prev ? deltaPct(volumeTon, nano(prev.volumeNano)) : null}
                color="var(--info)"
              />
              <Kpi
                icon={<DollarSign size={18} aria-hidden="true" />}
                label={t('partner.overview.revenue')}
                value={
                  revenueUsd != null
                    ? `$${formatInt(Math.round(revenueUsd))}`
                    : `${formatTon(commissionGram(onchain?.volumeNano ?? '0', onchain?.commissionNano))} GRAM`
                }
                spark={volumeSpark}
                delta={
                  revenueUsd != null && prevRevenueUsd != null
                    ? deltaPct(revenueUsd, prevRevenueUsd)
                    : null
                }
                color="var(--info)"
              />
              <Kpi
                icon={<Percent size={18} aria-hidden="true" />}
                label={t('partner.overview.conversion')}
                value={conversionPct != null ? `${conversionPct.toFixed(conversionPct >= 10 ? 0 : 1)}%` : '—'}
              />
              <Kpi
                icon={<Target size={18} aria-hidden="true" />}
                label={t('partner.overview.avgCheck')}
                value={avgCheckTon != null ? `${formatTon(avgCheckTon)} GRAM` : '—'}
              />
              <Kpi
                icon={<MousePointerClick size={18} aria-hidden="true" />}
                label={t('partner.overview.epc')}
                value={epcUsd != null ? `$${epcUsd.toFixed(3)}` : '—'}
              />
            </div>
          )}

          {/* Trend chart (multi-metric + previous-period overlay) */}
          {hasApi && !showSkeleton && (
            <div className="pl-card">
              <div className="pl-card__head">
                <h3 className="pl-h3">
                  <TrendingUp size={18} aria-hidden="true" /> {t('partner.overview.trendTitle')}
                </h3>
                <div className="pl-metric-switch" role="group" aria-label={t('partner.overview.trendTitle')}>
                  {(['signups', 'purchases', 'volume', 'earnings'] as TrendMetric[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`pl-range__btn ${trendMetric === m ? 'is-active' : ''}`}
                      onClick={() => setTrendMetric(m)}
                    >
                      {t(`partner.overview.metric.${m}`)}
                    </button>
                  ))}
                </div>
              </div>
              {trendPoints.some((p) => p.value > 0) ? (
                <>
                  <AreaChart
                    points={trendPoints}
                    previous={prevTrend}
                    format={trendFmt}
                    label={t('partner.overview.trendTitle')}
                  />
                  {prevTrend.some((v) => v > 0) && (
                    <p className="pl-chart__legend">
                      <span className="pl-chart__legend-prev" aria-hidden="true" />
                      {t('partner.overview.prevPeriod')}
                    </p>
                  )}
                </>
              ) : (
                <p className="pl-muted">{t('partner.overview.trendEmpty')}</p>
              )}
            </div>
          )}

          {/* Funnel */}
          {hasApi && !showSkeleton && (
            <div className="pl-card">
              <h3 className="pl-h3">
                <Send size={18} aria-hidden="true" /> {t('partner.overview.funnelTitle')}
              </h3>
              <p className="pl-muted">{t('partner.overview.funnelHint')}</p>
              {funnel ? (
                <Funnel steps={funnelSteps} />
              ) : (
                <p className="pl-muted">{t('partner.panel.statsUnavailable')}</p>
              )}
            </div>
          )}

          {/* Per-source performance table (merged from the old Reports tab) */}
          {hasApi && !showSkeleton && (
            <div className="pl-card">
              <div className="pl-card__head">
                <h3 className="pl-h3">
                  <BarChart3 size={18} aria-hidden="true" /> {t('partner.subs.title')}
                </h3>
                {visibleSubs.length > 0 && (
                  <button type="button" className="pl-btn pl-btn--ghost" onClick={exportSubsCsv}>
                    <Download size={15} aria-hidden="true" /> {t('partner.export')}
                  </button>
                )}
              </div>
              <p className="pl-muted pl-subs-note">{t('partner.subs.note')}</p>
              {visibleSubs.length === 0 ? (
                <p className="pl-muted">{t('partner.subs.empty')}</p>
              ) : (
                <div className="pl-table-wrap">
                  <table className="pl-table pl-table--pro">
                    <thead>
                      <tr>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('sub')}>
                            {t('partner.subs.colSub')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('visitors')}>
                            {t('partner.subs.colVisitors')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('purchases')}>
                            {t('partner.subs.colPurchases')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('cr')}>
                            {t('partner.subs.colCr')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('revenue')}>
                            {t('partner.subs.colRevenue')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('epc')}>
                            {t('partner.subs.colEpc')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('cost')}>
                            {t('partner.subs.colCost')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="pl-th-sort" onClick={() => toggleSort('roi')}>
                            {t('partner.subs.colRoi')}
                          </button>
                        </th>
                        <th>{t('partner.subs.colCpa')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSubs.map((s) => (
                        <tr key={s.subId}>
                          <td>
                            <code>{s.subId}</code>
                          </td>
                          <td>{formatInt(s.visitors)}</td>
                          <td>{formatInt(s.purchases)}</td>
                          <td>{s.cr != null ? `${s.cr.toFixed(s.cr >= 10 ? 0 : 1)}%` : '—'}</td>
                          <td>{s.revenueUsd != null ? `$${formatInt(Math.round(s.revenueUsd))}` : '—'}</td>
                          <td>{s.epc != null ? `$${s.epc.toFixed(3)}` : '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="pl-cost-btn"
                              onClick={() => openSpendModal(s.subId)}
                              aria-label={t('partner.subs.costAria', { source: s.subId })}
                            >
                              {s.costUsd > 0 ? (
                                `$${formatInt(Math.round(s.costUsd))}`
                              ) : (
                                <>
                                  <Plus size={13} aria-hidden="true" /> {t('partner.spend.add')}
                                </>
                              )}
                            </button>
                          </td>
                          <td>
                            {s.roi != null ? (
                              <span className={s.roi >= 0 ? 'pl-roi is-up' : 'pl-roi is-down'}>
                                {s.roi >= 0 ? '+' : ''}
                                {s.roi.toFixed(0)}%
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{s.cpa != null ? `$${s.cpa.toFixed(2)}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Live activity feed */}
          {hasApi && !showSkeleton && (
            <div className="pl-card">
              <h3 className="pl-h3">
                <Activity size={18} aria-hidden="true" /> {t('partner.feed.title')}
              </h3>
              {overviewActivity.length === 0 ? (
                <p className="pl-muted">{t('partner.feed.empty')}</p>
              ) : (
                <>
                  <ul className="pl-feed">
                    {overviewActivity.slice(0, 12).map((a, i) => (
                      <li key={`${a.event}-${a.subId}-${a.at}-${i}`} className="pl-feed__item">
                        <span className={`pl-feed__dot ${statusClass(a.status)}`} aria-hidden="true" />
                        <span className="pl-feed__event">{eventLabel(a.event)}</span>
                        <code className="pl-feed__sub">{a.subId}</code>
                        <time className="pl-feed__time">
                          {formatDateTime(Math.floor(new Date(a.at).getTime() / 1000))}
                        </time>
                      </li>
                    ))}
                  </ul>
                  <p className="pl-muted pl-note">
                    {t('partner.feed.showingLast', {
                      count: Math.min(overviewActivity.length, 12),
                    })}
                  </p>
                </>
              )}
            </div>
          )}

          {!hasApi && (
            <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
          )}
        </div>
      )}

      {/* ── LINKS & SOURCES ──────────────────────────────────────── */}
      {tab === 'links' && (
        <div className="pl-tabpanel" role="tabpanel">
          {/* Link builder: pick (or type) one source, get its four links once. */}
          <div className="pl-card">
            <h3 className="pl-h3">
              <Link2 size={18} aria-hidden="true" /> {t('partner.links.title')}
            </h3>
            <p className="pl-muted">{t('partner.links.builderHint')}</p>

            {/* Source selector: default + saved sources + a "custom SubID" mode. */}
            <div
              className="pl-srcpick"
              role="group"
              aria-label={t('partner.links.sourceLabel')}
            >
              <button
                type="button"
                className={`pl-srcpick__chip${!customMode && trimmedSub === 'main' ? ' is-active' : ''}`}
                onClick={() => pickSource('main')}
              >
                {t('partner.sources.mainLabel')}
                <code>main</code>
              </button>
              {sources.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`pl-srcpick__chip${!customMode && trimmedSub === s.subId ? ' is-active' : ''}`}
                  onClick={() => pickSource(s.subId)}
                >
                  {s.label.trim() || s.subId}
                  <code>{s.subId}</code>
                </button>
              ))}
              <button
                type="button"
                className={`pl-srcpick__chip pl-srcpick__chip--custom${customMode ? ' is-active' : ''}`}
                onClick={enterCustomMode}
              >
                <Plus size={14} aria-hidden="true" /> {t('partner.links.customSource')}
              </button>
            </div>

            {/* Custom SubID input (only in custom mode), with save-as-source. */}
            {customMode && (
              <div className="pl-srccustom">
                <input
                  type="text"
                  value={subId}
                  onChange={(e) => setSubId(sanitizeSource(e.target.value))}
                  spellCheck={false}
                  maxLength={32}
                  placeholder={t('partner.links.subIdLabel')}
                  aria-label={t('partner.links.subIdLabel')}
                  autoFocus
                />
                {canSaveCustom && (
                  <>
                    <input
                      type="text"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      maxLength={60}
                      placeholder={t('partner.sources.labelPlaceholder')}
                      aria-label={t('partner.sources.labelPlaceholder')}
                    />
                    <button
                      type="button"
                      className="pl-btn pl-btn--gold"
                      onClick={() => void handleSaveCustomSource()}
                      disabled={srcBusy || !newLabel.trim()}
                    >
                      <Plus size={15} aria-hidden="true" /> {t('partner.links.saveSource')}
                    </button>
                  </>
                )}
              </div>
            )}
            {srcError && <span className="pl-error">{srcError}</span>}
            <small className="pl-srccustom__hint">{t('partner.links.subIdHint')}</small>

            {/* Which source these links belong to (keeps the selection visible). */}
            <div className="pl-srcactive">
              <span className="pl-srcactive__label">{t('partner.links.activeSource')}</span>
              <strong className="pl-srcactive__name">
                {customMode
                  ? trimmedSub || t('partner.links.customSource')
                  : (activeSource?.label.trim() ?? t('partner.sources.mainLabel'))}
              </strong>
              <code className="pl-srcactive__sub">{trimmedSub || 'main'}</code>
            </div>

            {/* Drop the {click_id} macro for dashboard-only partners. */}
            <label className="pl-usemain pl-cid-toggle">
              <input
                type="checkbox"
                checked={withClickId}
                onChange={(e) => setWithClickId(e.target.checked)}
              />
              <span>{t('partner.links.withClickId')}</span>
            </label>
            <small className="pl-srccustom__hint">{t('partner.links.withClickIdHint')}</small>

            {/* The four tracking links for the active source. */}
            <CopyLinkRow
              label={t('partner.links.bot')}
              hint={t('partner.links.botHint')}
              link={links.bot}
            />
            <CopyLinkRow
              label={t('partner.links.app')}
              hint={t('partner.links.appHint')}
              link={links.app}
            />
            <CopyLinkRow
              label={t('partner.links.team')}
              hint={t('partner.links.teamHint')}
              link={links.team}
            />
            <CopyLinkRow
              label={t('partner.links.site')}
              hint={t('partner.links.siteHint')}
              link={links.site}
            />
            <CopyLinkRow
              label={t('partner.links.partner')}
              hint={t('partner.links.partnerHint')}
              link={links.partner}
            />

            {/* Delete the active named source in place (default can't be deleted). */}
            {activeSource && (
              <div className="pl-srcdelete">
                <button
                  type="button"
                  className="pl-btn pl-btn--ghost pl-btn--danger"
                  onClick={() => void handleDeleteSource(activeSource.id)}
                >
                  <Trash2 size={15} aria-hidden="true" />
                  {t('partner.sources.deleteNamed', {
                    name: activeSource.label.trim() || activeSource.subId,
                  })}
                </button>
                <small className="pl-srcdelete__note">{t('partner.sources.deleteNote')}</small>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CREATIVES (promo kit) ────────────────────────────────── */}
      {tab === 'creatives' && (
        <div className="pl-tabpanel" role="tabpanel">
          <div className="pl-card">
            <h3 className="pl-h3">
              <ImageIcon size={18} aria-hidden="true" /> {t('partner.creatives.title')}
            </h3>
            <p className="pl-muted">{t('partner.creatives.hint')}</p>

            {/* Destination picker for the QR */}
            <div className="pl-range" role="group" aria-label={t('partner.creatives.pickLink')}>
              {PROMO_LINKS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`pl-range__btn ${promoLink === l ? 'is-active' : ''}`}
                  onClick={() => setPromoLink(l)}
                >
                  {t(`partner.links.${l}`)}
                </button>
              ))}
            </div>

            {/* QR source: per-type default (qr4*) or one of the partner's own sources */}
            <label className="pl-field pl-creatives__srcpick">
              <span>{t('partner.creatives.qrSourceLabel')}</span>
              <select
                value={qrSource}
                onChange={(e) => setQrSource(e.target.value)}
              >
                <option value={QR_SOURCE_AUTO}>{t('partner.creatives.qrSourceAuto')}</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.subId}>
                    {s.label} ({s.subId})
                  </option>
                ))}
              </select>
            </label>

            <div className="pl-creatives">
              <div className="pl-creatives__qr">
                <ReferralQr ref={qrCanvasRef} value={qrLinks[promoLink]} size={220} />
                <button
                  type="button"
                  className="pl-btn pl-btn--gold"
                  onClick={() => {
                    void (async () => {
                      const url = await renderQrDataUrl(qrLinks[promoLink], 1024);
                      const name = `pyramida-qr-${refKey}.png`;
                      if (isTelegramMiniApp()) {
                        try {
                          const blob = await (await fetch(url)).blob();
                          const file = new File([blob], name, { type: 'image/png' });
                          if (navigator.canShare?.({ files: [file] })) {
                            await navigator.share({ files: [file] });
                            return;
                          }
                        } catch (err) {
                          if (err instanceof DOMException && err.name === 'AbortError') return;
                        }
                        setQrSavePreview(url);
                        return;
                      }
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = name;
                      a.click();
                    })();
                  }}
                >
                  <Download size={15} aria-hidden="true" /> {t('partner.creatives.downloadQr')}
                </button>
              </div>
              <div className="pl-creatives__copy">
                <CopyLinkRow label={t(`partner.links.${promoLink}`)} link={qrLinks[promoLink]} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ON-CHAIN (network tree → commissions → cohorts/LTV) ──── */}
      {tab === 'onchain' && (
        <div className="pl-tabpanel" role="tabpanel">
          {/* 1) Downline network by level */}
          <div className="pl-card">
            <h3 className="pl-h3">
              <Share2 size={18} aria-hidden="true" /> {t('partner.growth.networkTitle')}
            </h3>
            <p className="pl-muted">{t('partner.growth.networkHint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : network === undefined ? (
              <div className="pl-card pl-skeleton" style={{ height: 120 }} aria-hidden="true" />
            ) : network === null ? (
              <p className="pl-muted">{t('partner.panel.statsUnavailable')}</p>
            ) : network.length === 0 ? (
              <p className="pl-muted">{t('partner.growth.networkEmpty')}</p>
            ) : (
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>{t('partner.growth.colLevel')}</th>
                      <th>{t('partner.growth.colInvited')}</th>
                      <th>{t('partner.growth.colPartners')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {network.map((n) => (
                      <tr key={n.level}>
                        <td>
                          <span className="pl-level-badge">L{n.level}</span>
                        </td>
                        <td>{formatInt(n.invited)}</td>
                        <td>{formatInt(n.partners)}</td>
                      </tr>
                    ))}
                    <tr className="pl-table__total">
                      <td>{t('partner.growth.total')}</td>
                      <td>{formatInt(sum(network.map((n) => n.invited)))}</td>
                      <td>{formatInt(sum(network.map((n) => n.partners)))}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="pl-muted pl-table-legend">{t('partner.growth.networkLegend')}</p>
              </div>
            )}
          </div>

          {/* 2) On-chain commissions */}
          <div className="pl-card">
            <div className="pl-card__head">
              <h3 className="pl-h3">
                <Coins size={18} aria-hidden="true" /> {t('partner.network.title')}
              </h3>
            </div>
            <p className="pl-muted">{t('partner.network.hint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : commissions === null ? (
              <div className="pl-card pl-skeleton" style={{ height: 120 }} aria-hidden="true" />
            ) : commissions.length === 0 ? (
              <p className="pl-muted">{t('partner.network.empty')}</p>
            ) : (
              <>
                <div className="pl-table-wrap">
                  <table className="pl-table">
                    <thead>
                      <tr>
                        <th>{t('partner.network.colDate')}</th>
                        <th>{t('partner.network.colBuyer')}</th>
                        <th>{t('partner.network.colAmount')}</th>
                        <th aria-label="link" />
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.slice(0, 10).map((c) => (
                        <tr key={c.txHash}>
                          <td>{formatDateTime(c.utime)}</td>
                          <td>
                            <code>{shortenAddress(c.buyer)}</code>
                          </td>
                          <td>{formatTon(nano(c.amountNano))} GRAM</td>
                          <td>
                            {c.txHash && (
                              <a
                                className="pl-iconbtn"
                                href={tonviewerTx(c.txHash)}
                                target="_blank"
                                rel="noreferrer noopener"
                                aria-label={t('partner.network.openTx')}
                                title={t('partner.network.openTx')}
                              >
                                <ExternalLink size={14} aria-hidden="true" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {commissions.length > 10 && (
                  <p className="pl-muted pl-note">
                    {t('partner.network.showingLast', { count: 10 })}
                  </p>
                )}

                {/* Dated CSV export lives at the foot of the widget. */}
                <div className="pl-commexport">
                  <span className="pl-commexport__label">{t('partner.network.exportRange')}</span>
                  <div
                    className="pl-daterange"
                    role="group"
                    aria-label={t('partner.network.exportRange')}
                  >
                    <DatePicker
                      value={commExportFrom}
                      max={commExportTo || todayStr}
                      ariaLabel={t('partner.range.from')}
                      placeholder={t('partner.range.from')}
                      onChange={setCommExportFrom}
                    />
                    <span className="pl-daterange__sep">–</span>
                    <DatePicker
                      value={commExportTo}
                      min={commExportFrom || undefined}
                      max={todayStr}
                      ariaLabel={t('partner.range.to')}
                      placeholder={t('partner.range.to')}
                      onChange={setCommExportTo}
                    />
                    <button
                      type="button"
                      className="pl-btn pl-btn--ghost"
                      onClick={exportCommissionsCsv}
                    >
                      <Download size={15} aria-hidden="true" /> {t('partner.export')}
                    </button>
                  </div>
                  <small className="pl-commexport__hint">{t('partner.network.exportHint')}</small>
                </div>
              </>
            )}
          </div>

          {/* 3) Acquisition cohorts / LTV */}
          <div className="pl-card">
            <div className="pl-card__head">
              <h3 className="pl-h3">
                <TrendingUp size={18} aria-hidden="true" /> {t('partner.growth.cohortsTitle')}
              </h3>
              {cohorts && cohorts.length > 0 && (
                <button
                  type="button"
                  className="pl-btn pl-btn--ghost"
                  onClick={() =>
                    downloadCsv(`pyramida-cohorts-${refKey}.csv`, [
                      ['cohort', 'buyers', 'purchases', 'volume_ton', 'avg_ltv_ton'],
                      ...cohorts.map((c) => [
                        c.cohort,
                        c.buyers,
                        c.purchases,
                        (nano(c.volumeNano)).toString(),
                        (nano(c.volumeNano) / Math.max(1, c.buyers)).toString(),
                      ]),
                    ])
                  }
                >
                  <Download size={15} aria-hidden="true" /> {t('partner.export')}
                </button>
              )}
            </div>
            <p className="pl-muted">{t('partner.growth.cohortsHint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : cohorts === null ? (
              <div className="pl-card pl-skeleton" style={{ height: 120 }} aria-hidden="true" />
            ) : cohorts.length === 0 ? (
              <p className="pl-muted">{t('partner.growth.cohortsEmpty')}</p>
            ) : (
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>{t('partner.growth.colCohort')}</th>
                      <th>{t('partner.growth.colBuyers')}</th>
                      <th>{t('partner.growth.colPurchases')}</th>
                      <th>{t('partner.growth.colVolume')}</th>
                      <th>{t('partner.growth.colLtv')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cohorts.map((c) => (
                      <tr key={c.cohort}>
                        <td>{c.cohort}</td>
                        <td>{formatInt(c.buyers)}</td>
                        <td>{formatInt(c.purchases)}</td>
                        <td>{formatTon(nano(c.volumeNano))} GRAM</td>
                        <td>{formatTon(nano(c.volumeNano) / Math.max(1, c.buyers))} GRAM</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── POSTBACKS ────────────────────────────────────────────── */}
      {tab === 'postbacks' && (
        <div className="pl-tabpanel" role="tabpanel">
          <div className="pl-card">
            <h3 className="pl-h3">
              <Webhook size={18} aria-hidden="true" /> {t('partner.postbacks.title')}
            </h3>
            <p className="pl-muted">{t('partner.postbacks.hint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : (
              <>
                <div className="pl-postback-main">
                  <div className="pl-postback-main__head">
                    <span className="pl-src__label">{t('partner.postbacks.mainTitle')}</span>
                    <code className="pl-src__sub">main</code>
                  </div>
                  <p className="pl-muted pl-postback-main__hint">
                    {t('partner.postbacks.mainHint')}
                  </p>
                  <PostbackEditor
                    events={events}
                    onEvent={setEvent}
                    scope="main"
                    onTest={handleTest}
                    testResults={testResults}
                  />
                  <div className="pl-save-row">
                    <button
                      type="button"
                      className="pl-btn pl-btn--gold"
                      onClick={() => void handleSave()}
                      disabled={saveState === 'saving'}
                    >
                      {saveState === 'saved' ? (
                        <>
                          <Check size={15} aria-hidden="true" /> {t('partner.postbacks.saved')}
                        </>
                      ) : (
                        t('partner.postbacks.save')
                      )}
                    </button>
                    {saveState === 'error' && (
                      <span className="pl-error">
                        {t('partner.postbacks.saveError', { error: saveError })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="pl-postback-sources">
                  <h4 className="pl-h4">{t('partner.postbacks.sourcesTitle')}</h4>
                  <p className="pl-muted">{t('partner.postbacks.sourcesHint')}</p>
                  {sources.length === 0 ? (
                    <p className="pl-muted pl-postback-sources__empty">
                      {t('partner.postbacks.sourcesEmpty')}
                    </p>
                  ) : (
                    <div className="pl-srclist">
                      {sources.map((s) => (
                        <SourcePostbackCard
                          key={s.id}
                          source={s}
                          token={token}
                          onSaved={handleSourceSaved}
                          onTest={handleTest}
                          testResults={testResults}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Delivery log */}
          {token && (
            <div className="pl-card">
              <h3 className="pl-h3">
                <Activity size={18} aria-hidden="true" /> {t('partner.log.title')}
              </h3>
              {firedActivity.length === 0 ? (
                <p className="pl-muted">{t('partner.log.empty')}</p>
              ) : (
                <div className="pl-table-wrap">
                  <table className="pl-table">
                    <thead>
                      <tr>
                        <th>{t('partner.log.colTime')}</th>
                        <th>{t('partner.log.colEvent')}</th>
                        <th>{t('partner.subs.colSub')}</th>
                        <th>{t('partner.log.colStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {firedActivity.map((a, i) => (
                        <tr key={`${a.event}-${a.subId}-${a.at}-${i}`}>
                          <td>{formatDateTime(Math.floor(new Date(a.at).getTime() / 1000))}</td>
                          <td>{eventLabel(a.event)}</td>
                          <td>
                            <code>{a.subId}</code>
                          </td>
                          <td>
                            <span className={`pl-status ${statusClass(a.status)}`}>
                              {a.status ?? '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── API & WEBHOOKS ───────────────────────────────────────── */}
      {tab === 'api' && (
        <div className="pl-tabpanel" role="tabpanel">
          {/* API keys */}
          <div className="pl-card">
            <h3 className="pl-h3">
              <KeyRound size={18} aria-hidden="true" /> {t('partner.api.keysTitle')}
            </h3>
            <p className="pl-muted">{t('partner.api.keysHint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : (
              <>
                {newKeySecret && (
                  <div className="pl-card pl-card--accent">
                    <p className="pl-muted">
                      <strong>{t('partner.api.newKeyTitle')}</strong> — {t('partner.api.newKeyHint')}
                    </p>
                    <CopyValue value={newKeySecret} />
                    <button
                      type="button"
                      className="pl-btn pl-btn--ghost"
                      onClick={() => setNewKeySecret('')}
                    >
                      {t('partner.api.dismiss')}
                    </button>
                  </div>
                )}
                <div className="pl-form-row">
                  <input
                    type="text"
                    className="pl-input"
                    placeholder={t('partner.api.labelPlaceholder')}
                    value={newKeyLabel}
                    maxLength={60}
                    onChange={(e) => setNewKeyLabel(e.target.value)}
                  />
                  <button
                    type="button"
                    className="pl-btn pl-btn--gold"
                    onClick={() => void handleCreateKey()}
                    disabled={keyBusy}
                  >
                    <Plus size={15} aria-hidden="true" /> {t('partner.api.generate')}
                  </button>
                </div>
                {keyError && <p className="pl-error">{keyError}</p>}
                {apiKeys === null ? (
                  <div className="pl-card pl-skeleton" style={{ height: 80 }} aria-hidden="true" />
                ) : apiKeys.length === 0 ? (
                  <p className="pl-muted">{t('partner.api.keysEmpty')}</p>
                ) : (
                  <div className="pl-table-wrap">
                    <table className="pl-table">
                      <thead>
                        <tr>
                          <th>{t('partner.api.colLabel')}</th>
                          <th>{t('partner.api.colKey')}</th>
                          <th>{t('partner.api.colCreated')}</th>
                          <th>{t('partner.api.colLastUsed')}</th>
                          <th aria-label={t('partner.api.colActions')} />
                        </tr>
                      </thead>
                      <tbody>
                        {apiKeys.map((k) => (
                          <tr key={k.id}>
                            <td>{k.label || '—'}</td>
                            <td>
                              <code>{k.keyPrefix}…</code>
                            </td>
                            <td>{formatDateTime(Math.floor(new Date(k.createdAt).getTime() / 1000))}</td>
                            <td>
                              {k.lastUsedAt
                                ? formatDateTime(Math.floor(new Date(k.lastUsedAt).getTime() / 1000))
                                : '—'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="pl-btn pl-btn--ghost pl-btn--danger"
                                onClick={() => void handleRevokeKey(k.id)}
                                aria-label={t('partner.api.revoke')}
                              >
                                <Trash2 size={15} aria-hidden="true" /> {t('partner.api.revoke')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="pl-docs">
                  <p className="pl-muted">{t('partner.api.docsHint')}</p>
                  <CopyValue value={`${API_BASE}/api/partner/v1/overview`} />
                  <pre className="pl-code-block">
{`curl -H "Authorization: Bearer prmd_live_…" \\
  ${API_BASE}/api/partner/v1/overview?days=30`}
                  </pre>
                  <p className="pl-muted">{t('partner.api.endpoints')}</p>
                  <ul className="pl-list">
                    <li>
                      <code>GET /v1/overview?days=</code>
                    </li>
                    <li>
                      <code>GET /v1/commissions?limit=</code>
                    </li>
                    <li>
                      <code>GET /v1/sources</code>
                    </li>
                    <li>
                      <code>GET /v1/network</code>
                    </li>
                    <li>
                      <code>GET /v1/cohorts?limit=</code>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* Webhooks */}
          <div className="pl-card">
            <h3 className="pl-h3">
              <Send size={18} aria-hidden="true" /> {t('partner.api.webhooksTitle')}
            </h3>
            <p className="pl-muted">{t('partner.api.webhooksHint')}</p>
            {!token ? (
              <p className="pl-muted pl-auth-note">{t('partner.postbacks.authRequired')}</p>
            ) : (
              <>
                <div className="pl-form-row">
                  <input
                    type="url"
                    className="pl-input"
                    placeholder="https://your-server.com/webhook"
                    value={newHookUrl}
                    onChange={(e) => setNewHookUrl(e.target.value)}
                  />
                  <button
                    type="button"
                    className="pl-btn pl-btn--gold"
                    onClick={() => void handleCreateWebhook()}
                    disabled={hookBusy || !newHookUrl.trim()}
                  >
                    <Plus size={15} aria-hidden="true" /> {t('partner.api.addWebhook')}
                  </button>
                </div>
                <p className="pl-muted pl-hint-sm">{t('partner.api.eventsHint')}</p>
                <div className="pl-chips">
                  {PARTNER_EVENTS.map((event) => (
                    <button
                      key={event}
                      type="button"
                      className={`pl-chip ${newHookEvents.includes(event) ? 'pl-chip--on' : ''}`}
                      onClick={() => toggleHookEvent(event)}
                    >
                      {t(`partner.events.${event}`)}
                    </button>
                  ))}
                </div>
                {hookError && <p className="pl-error">{hookError}</p>}

                {webhooks === null ? (
                  <div className="pl-card pl-skeleton" style={{ height: 80 }} aria-hidden="true" />
                ) : webhooks.length === 0 ? (
                  <p className="pl-muted">{t('partner.api.webhooksEmpty')}</p>
                ) : (
                  <div className="pl-webhooks">
                    {webhooks.map((w) => {
                      const test = hookTest[w.id];
                      return (
                        <div key={w.id} className="pl-card pl-card--nested">
                          <div className="pl-card__head">
                            <code className="pl-link__url">{w.url}</code>
                            <div className="pl-webhook__actions">
                              <button
                                type="button"
                                className="pl-btn pl-btn--ghost"
                                onClick={() => void handleTestWebhook(w.id)}
                                disabled={test === 'testing'}
                              >
                                <FlaskConical size={15} aria-hidden="true" /> {t('partner.postbacks.test')}
                              </button>
                              <button
                                type="button"
                                className="pl-btn pl-btn--ghost pl-btn--danger"
                                onClick={() => void handleDeleteWebhook(w.id)}
                                aria-label={t('partner.api.deleteWebhook')}
                              >
                                <Trash2 size={15} aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                          {test && test !== 'testing' && (
                            <p className={`pl-status ${statusClass(test.status)}`}>
                              {test.status ?? '—'} {test.error ? `· ${test.error}` : ''}
                            </p>
                          )}
                          <p className="pl-muted pl-hint-sm">
                            {w.events.length === 0
                              ? t('partner.api.allEvents')
                              : w.events.map((e: PartnerEvent) => eventLabel(e)).join(', ')}
                          </p>
                          <div className="pl-webhook__secret">
                            <span className="pl-link__hint">{t('partner.api.signingSecret')}</span>
                            <CopyValue value={w.secret} />
                          </div>
                          <button
                            type="button"
                            className="pl-btn pl-btn--ghost pl-btn--sm"
                            onClick={() => void handleToggleDeliveries(w.id)}
                          >
                            {deliveries[w.id]
                              ? t('partner.api.hideDeliveries')
                              : t('partner.api.showDeliveries')}
                          </button>
                          {deliveries[w.id] && (
                            deliveries[w.id].length === 0 ? (
                              <p className="pl-muted">{t('partner.api.deliveriesEmpty')}</p>
                            ) : (
                              <div className="pl-table-wrap">
                                <table className="pl-table">
                                  <thead>
                                    <tr>
                                      <th>{t('partner.log.colTime')}</th>
                                      <th>{t('partner.log.colEvent')}</th>
                                      <th>{t('partner.log.colStatus')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {deliveries[w.id].map((d, i) => (
                                      <tr key={`${d.event}-${d.createdAt}-${i}`}>
                                        <td>
                                          {formatDateTime(
                                            Math.floor(new Date(d.createdAt).getTime() / 1000),
                                          )}
                                        </td>
                                        <td>{eventLabel(d.event)}</td>
                                        <td>
                                          <span className={`pl-status ${statusClass(d.status)}`}>
                                            {d.status ?? (d.error ? '✗' : '—')}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="pl-docs">
                  <p className="pl-muted">{t('partner.api.sigHint')}</p>
                  <pre className="pl-code-block">
{`X-Pyramida-Event: ticket_purchase
X-Pyramida-Signature: t=<unix>,v1=<hmac-sha256(t + "." + body)>`}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Dated ad-spend manager (per source) */}
      {spendModal !== null && (
        <div
          className="pl-modal__backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSpendModal();
          }}
        >
          <div className="pl-modal" role="dialog" aria-modal="true" aria-label={t('partner.spend.title')}>
            <div className="pl-modal__head">
              <h3 className="pl-h3">
                <DollarSign size={18} aria-hidden="true" /> {t('partner.spend.title')}{' '}
                <code>{spendModal}</code>
              </h3>
              <button
                type="button"
                className="pl-btn pl-btn--ghost pl-btn--sm"
                onClick={closeSpendModal}
                aria-label={t('partner.spend.close')}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <p className="pl-muted pl-hint-sm">{t('partner.spend.hint')}</p>

            <div className="pl-spend-form">
              <div className="pl-costcell">
                <span className="pl-costcell__prefix">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="pl-cost-input"
                  placeholder={t('partner.spend.amount')}
                  aria-label={t('partner.spend.amount')}
                  value={spendDraft.amount}
                  onChange={(e) => setSpendDraft((d) => ({ ...d, amount: e.target.value }))}
                />
              </div>
              <DatePicker
                value={spendDraft.from}
                max={spendDraft.to || todayStr}
                ariaLabel={t('partner.range.from')}
                placeholder={t('partner.range.from')}
                onChange={(v) => setSpendDraft((d) => ({ ...d, from: v }))}
              />
              <span className="pl-daterange__sep">–</span>
              <DatePicker
                value={spendDraft.to}
                min={spendDraft.from || undefined}
                max={todayStr}
                ariaLabel={t('partner.range.to')}
                placeholder={t('partner.range.to')}
                onChange={(v) => setSpendDraft((d) => ({ ...d, to: v }))}
              />
              <button
                type="button"
                className="pl-btn"
                onClick={() => void handleAddSpend()}
                disabled={spendBusy || !spendDraft.amount || !spendDraft.from || !spendDraft.to}
              >
                <Plus size={15} aria-hidden="true" /> {t('partner.spend.add')}
              </button>
            </div>
            {spendError && <p className="pl-error">{spendError}</p>}

            {spend.filter((e) => e.subId === spendModal).length === 0 ? (
              <p className="pl-muted">{t('partner.spend.empty')}</p>
            ) : (
              <ul className="pl-spend-list">
                {spend
                  .filter((e) => e.subId === spendModal)
                  .map((e) => (
                    <li key={e.id} className="pl-spend-item">
                      <span className="pl-spend-item__amount">${formatTon(e.costUsd)}</span>
                      <span className="pl-spend-item__dates">
                        {e.fromDate === e.toDate ? e.fromDate : `${e.fromDate} – ${e.toDate}`}
                      </span>
                      <button
                        type="button"
                        className="pl-btn pl-btn--ghost pl-btn--danger pl-btn--sm"
                        onClick={() => void handleDeleteSpend(e.id)}
                        aria-label={t('partner.spend.delete')}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Long-press-to-save QR view (Mini App webviews block downloads) */}
      {qrSavePreview !== null && (
        <div
          className="pl-modal__backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setQrSavePreview(null);
          }}
        >
          <div
            className="pl-modal pl-modal--qr"
            role="dialog"
            aria-modal="true"
            aria-label={t('partner.creatives.saveTitle')}
          >
            <div className="pl-modal__head">
              <h3 className="pl-h3">
                <Download size={18} aria-hidden="true" /> {t('partner.creatives.saveTitle')}
              </h3>
              <button
                type="button"
                className="pl-btn pl-btn--ghost pl-btn--sm"
                onClick={() => setQrSavePreview(null)}
                aria-label={t('partner.spend.close')}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <img className="pl-qr-save__img" src={qrSavePreview} alt="" />
            <p className="pl-muted pl-hint-sm">{t('partner.creatives.saveHint')}</p>
          </div>
        </div>
      )}
      <SupportLink variant="hint" />
    </section>
  );
}
